import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["className", "defaultValue", "defaultMonth", "disabledDays", "maxDate", "minDate", "modifiers", "onChange", "value"];
import React, { forwardRef } from 'react';
import { arrayOf, func, instanceOf, object, objectOf, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import { DayPicker } from 'react-day-picker';
import { endOfDay, isAfter, isBefore, isWithinInterval, startOfDay, format } from 'date-fns';
import { useControlled } from '@material-ui/core/utils';
import useStyles from './DatePicker.styles';
const isDateInRange = function (date, from, to) {
  if (from && to) {
    return isWithinInterval(date, {
      start: startOfDay(from),
      end: endOfDay(to)
    });
  }
  if (from) {
    return isAfter(date, startOfDay(from));
  }
  if (to) {
    return isBefore(date, endOfDay(to));
  }
  return true;
};

// generate the months abbreviations for the header
const formatCaption = function (month, options) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, format(month, 'MMM y', {
    locale: options?.locale
  }));
};
const labelDay = function (date, options) {
  return format(date, 'ccc MMM dd yyyy', {
    locale: options?.locale
  });
};
const DatePicker = /*#__PURE__*/forwardRef(function (props, ref) {
  const {
      className,
      defaultValue,
      defaultMonth,
      disabledDays: disabledDaysProp,
      maxDate,
      minDate,
      modifiers,
      onChange,
      value
    } = props,
    otherProps = _objectWithoutProperties(props, _excluded);
  const classes = useStyles();
  const [internalValue, setInternalValue] = useControlled({
    controlled: value,
    default: defaultValue,
    name: 'DatePicker'
  });
  const disabledDaysFn = function (date) {
    return !isDateInRange(date, minDate, maxDate);
  };
  const disabledDays = Array.isArray(disabledDaysProp) ? [disabledDaysFn, ...disabledDaysProp] : [disabledDaysFn, disabledDaysProp];
  return /*#__PURE__*/React.createElement(DayPicker, Object.assign({}, otherProps, {
    ref: ref,
    className: classNames('pds-date-picker', className),
    classNames: classes,
    defaultMonth: defaultMonth,
    disabled: disabledDays,
    formatters: {
      formatCaption
    },
    fromMonth: minDate,
    labels: {
      labelDay
    },
    modifiers: modifiers,
    onDayClick: function (day, dayModifiers) {
      // we need to match against our custom class
      if (dayModifiers[classes.day_disabled]) {
        return;
      }
      if (!dayModifiers.disabled) {
        setInternalValue(day);
        if (onChange) {
          onChange(day);
        }
      }
    },
    selected: internalValue,
    toMonth: maxDate
  }));
});
DatePicker.propTypes = {
  /**
   * The CSS class to add to the root node of the component.
   */
  className: string,
  /**
   * The month that will be shown when the component is mounted, __if no value__
   * __is provided the current month will be shown.__
   *
   * If you change the value after the component is mounted it is ignored.
   */
  defaultMonth: instanceOf(Date),
  /**
   * The selected date when the component is mounted. This is only useful in uncontrolled mode.
   */
  defaultValue: instanceOf(Date),
  /**
   * The days or range of days we want to set as disabled in the calendar.
   *
   * It can be a `function`, `Date` instance, object with a `from` and `to` keys or a
   * combination of them in an array.
   */
  disabledDays: oneOfType([func, instanceOf(Date), shape({
    from: instanceOf(Date),
    to: instanceOf(Date)
  }), arrayOf(oneOfType([func, instanceOf(Date), shape({
    from: instanceOf(Date),
    to: instanceOf(Date)
  })]))]),
  /**
   * The maximum date a user will be able to select (inclusive).
   */
  maxDate: instanceOf(Date),
  /**
   * The minimum date a user will be able to select (inclusive)
   */
  minDate: instanceOf(Date),
  /**
   * An object where the key is the name of the modifier and the value a
   * function that accepts a Date object and returns `true` if it
   * should apply the modifier or `false` otherwise.
   *
   */
  modifiers: objectOf(oneOfType([func, instanceOf(Date), shape({
    from: instanceOf(Date),
    to: instanceOf(Date)
  })])),
  /**
   * The inline styles that will be applied to the day container for the modifiers
   * we define in the `modifiers` prop.
   */
  modifiersStyles: object,
  /**
   * Callback fired when the value is changed.
   *
   * @param {Date} date The selected day in the picker.
   */
  onChange: func,
  /**
   * The date that will be selected, required for a controlled component.
   *
   * __Don't forget to provide an `onChange` callback if you define this prop.__
   */
  value: instanceOf(Date)
};
export default DatePicker;